import { useEffect, useRef, useState } from "react";
import { getAccessToken, onTokenChange } from "../api/client";

export interface SseEvent<T = unknown> {
  type: string;
  payload: T;
  at: string;
}

const EVENT_TYPES = [
  "detection.updated",
  "alert.raised",
  "alert.resolved",
  "archive.created",
  "report.generated",
] as const;

/**
 * SSE 实时推送。
 * - 令牌变化（登录 / 静默刷新）时自动重建连接
 * - 断线 3 秒重连，重连时读取最新令牌
 * - 返回连接状态，供顶栏「实时」指示灯使用
 */
export function useSse(onEvent: (event: SseEvent) => void): { connected: boolean } {
  const [connected, setConnected] = useState(false);
  const handlerRef = useRef(onEvent);
  handlerRef.current = onEvent;

  useEffect(() => {
    let es: EventSource | null = null;
    let retryTimer: number | null = null;
    let closed = false;

    const connect = (): void => {
      const token = getAccessToken();
      if (!token || closed) {
        setConnected(false);
        return;
      }
      es = new EventSource(`/api/stream/events?access_token=${encodeURIComponent(token)}`);
      es.onopen = () => setConnected(true);
      es.onerror = () => {
        setConnected(false);
        es?.close();
        es = null;
        if (!closed) {
          retryTimer = window.setTimeout(connect, 3000);
        }
      };
      for (const type of EVENT_TYPES) {
        es.addEventListener(type, (event) => {
          try {
            handlerRef.current(JSON.parse((event as MessageEvent<string>).data) as SseEvent);
          } catch {
            // 忽略解析失败的事件
          }
        });
      }
    };

    // 登录 / 令牌刷新 → 重建连接（EventSource 无法更新请求头）
    const unsubscribe = onTokenChange(() => {
      es?.close();
      es = null;
      if (retryTimer !== null) window.clearTimeout(retryTimer);
      connect();
    });

    connect();

    return () => {
      closed = true;
      unsubscribe();
      if (retryTimer !== null) window.clearTimeout(retryTimer);
      es?.close();
      setConnected(false);
    };
  }, []);

  return { connected };
}
